/**
 * 登録済み 360度写真の選択から路線 / エリアを作るための幾何ヘルパー (docs/27 §8、pure)。
 *
 * - 路線: 選択点を撮影時刻順 (無ければファイル名順 → 登録順) に並べた折れ線と、その全長 (m)。
 * - エリア: 選択点の凸包 (Andrew's monotone chain)。点が 3 未満のときは小さな正方形で囲む。
 */

export interface LatLng {
  latitude: number;
  longitude: number;
}

export interface SelectionPoint {
  id: string;
  location: LatLng;
  capturedAt: Date | null;
  fileName: string | null;
  createdAt: Date | null;
}

const EARTH_RADIUS_M = 6_371_000;

function toRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

/** 2 地点間の直線距離 (m、haversine)。functions 側 `haversineDistanceM` と同式。 */
export function haversineM(a: LatLng, b: LatLng): number {
  const dLat = toRad(b.latitude - a.latitude);
  const dLng = toRad(b.longitude - a.longitude);
  const lat1 = toRad(a.latitude);
  const lat2 = toRad(b.latitude);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** 折れ線の全長 (m)。 */
export function polylineLengthM(coords: LatLng[]): number {
  let total = 0;
  for (let i = 1; i < coords.length; i++) {
    total += haversineM(coords[i - 1]!, coords[i]!);
  }
  return total;
}

/**
 * 路線の並び順: 撮影時刻 → ファイル名 (自然順) → 登録日時 → id。
 * 360度カメラは連番ファイル名 (R0010001.JPG 等) を付けるため、撮影時刻が無くても概ね撮影順になる。
 */
export function orderPointsForRoute<T extends SelectionPoint>(points: T[]): T[] {
  const collator = new Intl.Collator('ja', { numeric: true, sensitivity: 'base' });
  return [...points].sort((a, b) => {
    const ta = a.capturedAt?.getTime() ?? null;
    const tb = b.capturedAt?.getTime() ?? null;
    if (ta !== null && tb !== null && ta !== tb) return ta - tb;
    if (ta !== null && tb === null) return -1;
    if (ta === null && tb !== null) return 1;
    const fa = a.fileName ?? '';
    const fb = b.fileName ?? '';
    if (fa && fb && fa !== fb) return collator.compare(fa, fb);
    const ca = a.createdAt?.getTime() ?? 0;
    const cb = b.createdAt?.getTime() ?? 0;
    if (ca !== cb) return ca - cb;
    return a.id.localeCompare(b.id);
  });
}

/** 選択点から路線 (折れ線 + 全長)。 */
export function buildRouteFromSelection<T extends SelectionPoint>(
  points: T[],
): { ordered: T[]; lineCoordinates: LatLng[]; lengthM: number } {
  const ordered = orderPointsForRoute(points);
  const lineCoordinates = ordered.map((p) => ({
    latitude: p.location.latitude,
    longitude: p.location.longitude,
  }));
  return { ordered, lineCoordinates, lengthM: polylineLengthM(lineCoordinates) };
}

function cross(o: LatLng, a: LatLng, b: LatLng): number {
  return (
    (a.longitude - o.longitude) * (b.latitude - o.latitude) -
    (a.latitude - o.latitude) * (b.longitude - o.longitude)
  );
}

/** 凸包 (Andrew's monotone chain)。同一点・共線点は除く。3 点未満はそのまま返す。 */
export function convexHull(points: LatLng[]): LatLng[] {
  const unique = new Map<string, LatLng>();
  for (const p of points) unique.set(`${p.latitude},${p.longitude}`, p);
  const pts = [...unique.values()].sort(
    (a, b) => a.longitude - b.longitude || a.latitude - b.latitude,
  );
  if (pts.length < 3) return pts;
  const lower: LatLng[] = [];
  for (const p of pts) {
    while (lower.length >= 2 && cross(lower[lower.length - 2]!, lower[lower.length - 1]!, p) <= 0) {
      lower.pop();
    }
    lower.push(p);
  }
  const upper: LatLng[] = [];
  for (let i = pts.length - 1; i >= 0; i--) {
    const p = pts[i]!;
    while (upper.length >= 2 && cross(upper[upper.length - 2]!, upper[upper.length - 1]!, p) <= 0) {
      upper.pop();
    }
    upper.push(p);
  }
  lower.pop();
  upper.pop();
  return [...lower, ...upper];
}

/**
 * 選択点を囲む多角形。凸包が 3 点以上ならそれ、そうでなければ (点が 1〜2 個 / 共線) 各点を
 * `paddingM` で囲む矩形の凸包を返す (エリアとして保存できる形にする)。
 */
export function polygonForSelection(points: LatLng[], paddingM = 15): LatLng[] {
  const hull = convexHull(points);
  if (hull.length >= 3) return hull;
  const padded: LatLng[] = [];
  for (const p of points) {
    const dLat = paddingM / 111_320;
    const dLng = paddingM / (111_320 * Math.max(0.1, Math.cos(toRad(p.latitude))));
    padded.push(
      { latitude: p.latitude + dLat, longitude: p.longitude + dLng },
      { latitude: p.latitude + dLat, longitude: p.longitude - dLng },
      { latitude: p.latitude - dLat, longitude: p.longitude + dLng },
      { latitude: p.latitude - dLat, longitude: p.longitude - dLng },
    );
  }
  return convexHull(padded);
}
